import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './StudyConfigurationPage.css';
import { useProject } from '../context/ProjectContext';
import { fetchLookups } from './studyConfigurationApi';

import EmailGatewayTab from './components/tabs/EmailGatewayTab';
import EmailFormatsTab from './components/tabs/EmailFormatsTab';
import ActivitiesTab from './components/tabs/ActivitiesTab';
import EmailMappingTab from './components/tabs/EmailMappingTab';
import ProfilesTab from './components/tabs/ProfilesTab';
import OperationsTab from './components/tabs/OperationsTab';
import RoleAccessTab from './components/tabs/RoleAccessTab';
import StagesTab from './components/tabs/StagesTab';
import ProfileActivitiesTab from './components/tabs/ProfileActivitiesTab';
import ProfileStagesTab from './components/tabs/ProfileStagesTab';
import IssueCategoriesTab from './components/tabs/IssueCategoriesTab';
import PrioritiesTab from './components/tabs/PrioritiesTab';
import LetterTemplatesTab from './components/tabs/LetterTemplatesTab';
import VisitTypesTab from './components/tabs/VisitTypesTab';
import ProjectVisitTypesTab from './components/tabs/ProjectVisitTypesTab';
import ExternalApisTab from './components/tabs/ExternalApisTab';

const GLOBAL_CORE_TABS = [
  { id: 'issue-categories', label: 'Issue Categories' },
  { id: 'priorities', label: 'Priorities' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'visit-types', label: 'Visit Types' },
  { id: 'external-apis', label: 'External APIs' },
];

const OPERATION_TABS = [
  { id: 'operations', label: 'Menu Operations' },
  { id: 'role-access', label: 'Menu Access' },
];

const EMAIL_TABS = [
  { id: 'email-credential', label: 'Email Credential' },
  { id: 'email-formats', label: 'Email Templates' },
  { id: 'activities', label: 'Activities' },
  { id: 'email-mapping', label: 'Email Mapping' },
];

const TAB_GROUPS = [
  { label: 'Global', tabs: GLOBAL_CORE_TABS, isProject: false },
  { label: 'Operation', tabs: OPERATION_TABS, isProject: false },
  { label: 'Email', tabs: EMAIL_TABS, isProject: false },
];

const PROJECT_TABS = [
  { id: 'stages', label: 'Workflow Stages' },
  { id: 'project-visit-types', label: 'Project Visit Types' },
  { id: 'profile-activities', label: 'Profile Permissions' },
  { id: 'profile-stages', label: 'Profile Stage Access' },
  { id: 'letter-templates', label: 'Letter Templates' },
];

const PROJECT_TAB_IDS = new Set(PROJECT_TABS.map((t) => t.id));

const ALL_TABS = [
  ...GLOBAL_CORE_TABS,
  ...OPERATION_TABS,
  ...EMAIL_TABS,
  ...PROJECT_TABS,
];

const NO_PROJECT_MSG =
  'No project in session. Change project from Visit or Site pages (shown in the header).';

function resolveSessionProjectCode(projectInfo, currentProject) {
  const fromInfo = (projectInfo?.projectCode || projectInfo?.ProjectCode || '').toString().trim();
  if (fromInfo) return fromInfo;
  const cp = (currentProject || '').toString().trim();
  if (cp && cp !== 'all') return cp;
  return '';
}

const StudyConfigurationPage = () => {
  const { projectInfo, currentProject } = useProject();
  const [projectRevision, setProjectRevision] = useState(0);
  const projectCode = useMemo(
    () => resolveSessionProjectCode(projectInfo, currentProject),
    [projectInfo, currentProject, projectRevision]
  );

  const [activeTab, setActiveTab] = useState('issue-categories');
  const [tabsExpanded, setTabsExpanded] = useState(true);
  const [profiles, setProfiles] = useState([]);

  const activeTabLabel = useMemo(
    () => ALL_TABS.find((t) => t.id === activeTab)?.label || '',
    [activeTab]
  );

  const loadLookups = useCallback(() => {
    fetchLookups()
      .then((res) => setProfiles(res.data?.profiles || []))
      .catch(() => setProfiles([]));
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    const onProjectUpdated = () => setProjectRevision((n) => n + 1);
    window.addEventListener('projectUpdated', onProjectUpdated);
    return () => window.removeEventListener('projectUpdated', onProjectUpdated);
  }, []);

  const handleTabClick = (tabId, isProjectTab) => {
    if (isProjectTab && !projectCode) return;
    setActiveTab(tabId);
  };

  const renderTab = () => {
    if (PROJECT_TAB_IDS.has(activeTab) && !projectCode) {
      return <p className="sc-empty">{NO_PROJECT_MSG}</p>;
    }

    switch (activeTab) {
      case 'email-credential':
        return <EmailGatewayTab />;
      case 'email-formats':
        return <EmailFormatsTab />;
      case 'activities':
        return <ActivitiesTab />;
      case 'email-mapping':
        return <EmailMappingTab />;
      case 'profiles':
        return <ProfilesTab />;
      case 'operations':
        return <OperationsTab />;
      case 'role-access':
        return <RoleAccessTab />;
      case 'stages':
        return <StagesTab key={projectCode} projectCode={projectCode} />;
      case 'visit-types':
        return <VisitTypesTab />;
      case 'external-apis':
        return <ExternalApisTab />;
      case 'project-visit-types':
        return <ProjectVisitTypesTab key={projectCode} projectCode={projectCode} />;
      case 'profile-activities':
        return (
          <ProfileActivitiesTab
            key={projectCode}
            projectCode={projectCode}
            profiles={profiles}
          />
        );
      case 'profile-stages':
        return (
          <ProfileStagesTab
            key={projectCode}
            projectCode={projectCode}
            profiles={profiles}
          />
        );
      case 'issue-categories':
        return <IssueCategoriesTab />;
      case 'priorities':
        return <PrioritiesTab />;
      case 'letter-templates':
        return <LetterTemplatesTab key={projectCode} projectCode={projectCode} />;
      default:
        return null;
    }
  };

  const renderTabGroup = (tabs, groupLabel, isProjectGroup) => (
    <div className="sc-tab-group-column">
      <span className="sc-tab-group-label">{groupLabel}</span>
      <div className="sc-tab-list" role="tablist" aria-label={groupLabel}>
        {tabs.map((t) => {
          const disabled = isProjectGroup && !projectCode;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              disabled={disabled}
              className={`sc-tab sc-tab-stack ${activeTab === t.id ? 'active' : ''} ${disabled ? 'sc-tab-disabled' : ''}`}
              onClick={() => handleTabClick(t.id, isProjectGroup)}
              title={disabled ? NO_PROJECT_MSG : undefined}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="study-config-page">
      {!projectCode && (
        <div className="sc-alert sc-alert-info">{NO_PROJECT_MSG}</div>
      )}

      <div className="sc-tabs-collapse-bar">
        <button
          type="button"
          className="sc-tabs-collapse-toggle"
          onClick={() => setTabsExpanded((v) => !v)}
          aria-expanded={tabsExpanded}
          aria-controls="sc-config-tab-groups"
        >
          <span className="sc-tabs-collapse-chevron" aria-hidden="true">
            {tabsExpanded ? '▼' : '▶'}
          </span>
          Configuration tabs
          {!tabsExpanded && activeTabLabel ? (
            <span className="sc-tabs-collapse-active"> — {activeTabLabel}</span>
          ) : null}
        </button>
      </div>

      {tabsExpanded ? (
        <div
          id="sc-config-tab-groups"
          className="sc-tab-groups sc-tab-groups-columns"
        >
          {TAB_GROUPS.map((group) => renderTabGroup(group.tabs, group.label, group.isProject))}
          {renderTabGroup(PROJECT_TABS, 'Project', true)}
        </div>
      ) : null}

      <div className="sc-card">
        <div className="sc-tab-panel">{renderTab()}</div>
      </div>
    </div>
  );
};

export default StudyConfigurationPage;
